import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, TrendingUp, Shield, Network } from "lucide-react";

// ── The three platform archetypes ────────────────────────────
// Each is a story, not a formula.
// Numbers are concrete but representative — not live calculations.

const scenarios = [
  {
    id: "deposit",
    icon: TrendingUp,
    tag: "The Deposit Tax",
    platform: "Mid-market restaurant booking platform",
    volume: "15,000 bookings / month",
    painLabel: "Current state",
    pain: "Deposits on every booking. 12,000 reliable guests pay the same €0.25 gateway fee as the riskiest 3,000. Friction on the wrong customers.",
    flipLabel: "With ClearSlot",
    flip: "ClearSlot classifies 80% as low-risk. Deposit waived. Gateway fees gone. The 20% who warrant friction still see it.",
    math: [
      { label: "Trusted guests skip deposit",  value: "12,000 / month" },
      { label: "Gateway fees eliminated",       value: "€3,000 / month" },
      { label: "ClearSlot cost",                value: "€750 / month" },
    ],
    headline: "€2,250",
    headlineLabel: "net saving per month",
    headlineSub: "No friction for anyone who didn't deserve it.",
    colour: "emerald",
  },
  {
    id: "noshows",
    icon: Shield,
    tag: "The Empty Table",
    platform: "Restaurant marketplace, avg €65 cover",
    volume: "40,000 bookings / month",
    painLabel: "Current state",
    pain: "Deposits off — they kill conversion. 5% no-show rate = 2,000 empty covers a month. Restaurants feel it and re-evaluate the channel.",
    flipLabel: "With ClearSlot",
    flip: "Signaled bookings drop to 1% no-show. 1,600 covers recovered per month. Conversion unchanged.",
    math: [
      { label: "Covers rescued per month",   value: "1,600" },
      { label: "Revenue protected",          value: "€104,000" },
      { label: "ClearSlot cost",             value: "€2,000" },
    ],
    headline: "52×",
    headlineLabel: "return on cost",
    headlineSub: "€2,000 in. €104,000 of protected revenue out.",
    colour: "primary",
  },
  {
    id: "network",
    icon: Network,
    tag: "The Network Multiplier",
    platform: "Enterprise restaurant reservation platform",
    volume: "200,000 bookings / month",
    painLabel: "Current state",
    pain: "Your guest history is good but local. The guest you flagged last month is booking your top restaurant on three competing apps this week.",
    flipLabel: "With ClearSlot",
    flip: "Your event history joins the network. Every no-show recorded contributes to and benefits from network-wide signals. Guests that relocate become visible.",
    math: [
      { label: "Solo platform catch rate",     value: "~12%" },
      { label: "Network catch rate",           value: "~74%" },
      { label: "Signal quality multiplier",    value: "6.2×" },
    ],
    headline: "6×",
    headlineLabel: "more no-shows caught",
    headlineSub: "Same guests. Same behaviour. The network just sees further.",
    colour: "accent",
  },
];

const colourMap = {
  emerald: {
    tag:      "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    headline: "text-emerald-400",
    active:   "border-emerald-500/40 bg-emerald-500/5",
    math:     "bg-emerald-500/10 border-emerald-500/20",
    mathVal:  "text-emerald-400",
  },
  primary: {
    tag:      "bg-primary/10 text-primary border-primary/30",
    headline: "text-primary",
    active:   "border-primary/40 bg-primary/5",
    math:     "bg-primary/10 border-primary/20",
    mathVal:  "text-primary",
  },
  accent: {
    tag:      "bg-accent/10 text-accent border-accent/30",
    headline: "text-accent",
    active:   "border-accent/40 bg-accent/5",
    math:     "bg-accent/10 border-accent/20",
    mathVal:  "text-accent",
  },
};

// ── Component ─────────────────────────────────────────────────

const ROICalculator = () => {
  const [active, setActive] = useState(scenarios[0].id);
  const current = scenarios.find((s) => s.id === active)!;
  const c = colourMap[current.colour as keyof typeof colourMap];

  return (
    <section id="calculator" className="relative py-20 overflow-hidden bg-muted/30">
      <div className="absolute inset-0 bg-grid opacity-20" />

      <div className="container relative mx-auto px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14 text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <TrendingUp className="h-4 w-4" />
            Model
          </div>
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Pick the platform that
            <br />
            <span className="text-primary">looks most like yours.</span>
          </h2>
          <p className="mx-auto max-w-xl text-lg text-muted-foreground">
            Three platform archetypes. Switch tabs to model yours.
          </p>
        </motion.div>

        {/* ── Scenario tabs ── */}
        <div className="max-w-5xl mx-auto">

          {/* Tab selector */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {scenarios.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              const col = colourMap[s.colour as keyof typeof colourMap];
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`rounded-xl border px-5 py-4 text-left transition-all duration-200 ${
                    isActive
                      ? col.active + " shadow-sm"
                      : "border-border bg-card/40 hover:border-border/80 hover:bg-card/60"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isActive ? col.mathVal : "text-muted-foreground"}`} />
                    <div>
                      <p className={`text-sm font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                        {s.tag}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                        {s.volume}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active scenario detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}
              className={`rounded-xl border ${c.active} overflow-hidden`}
            >
              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">

                {/* Left: Story */}
                <div className="px-7 py-8 space-y-6">

                  {/* Platform type */}
                  <div>
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium mb-3 ${c.tag}`}>
                      {current.tag}
                    </span>
                    <p className="text-sm text-muted-foreground">{current.platform}</p>
                  </div>

                  {/* Pain */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      {current.painLabel}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {current.pain}
                    </p>
                  </div>

                  {/* Flip */}
                  <div>
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <ArrowRight className={`h-3 w-3 ${c.mathVal}`} />
                      {current.flipLabel}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {current.flip}
                    </p>
                  </div>

                </div>

                {/* Right: Numbers */}
                <div className="px-7 py-8 flex flex-col justify-between gap-8">

                  {/* Math breakdown */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                      The numbers
                    </p>
                    <div className="space-y-2">
                      {current.math.map((row, i) => (
                        <div
                          key={i}
                          className={`flex items-center justify-between rounded-lg border px-4 py-2.5 ${c.math}`}
                        >
                          <span className="text-sm text-muted-foreground">{row.label}</span>
                          <span className={`text-sm font-bold font-mono tabular-nums ${c.mathVal}`}>
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Headline result */}
                  <div className="rounded-xl border border-border bg-card/60 px-5 py-5 text-center">
                    <p className={`text-6xl font-bold tabular-nums mb-1 ${c.headline}`}>
                      {current.headline}
                    </p>
                    <p className="text-sm font-semibold text-foreground mb-2">
                      {current.headlineLabel}
                    </p>
                    <p className="text-xs text-muted-foreground italic">
                      {current.headlineSub}
                    </p>
                  </div>

                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer note */}
          <p className="mt-6 text-center text-xs text-muted-foreground/70 max-w-xl mx-auto">
            Figures based on each archetype's volume, cover value, and no-show rate. Substitute your own to model yours.
          </p>

        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
